import { BellIcon, DocumentDuplicateIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { SaveChanges } from 'components/SaveChanges'
import { companyName, SettingKey } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import {
  emptySmScreenConfig,
  getSmFieldTitle,
  getSmSectionTitle,
  ISmScreenConfig,
  isSmFieldVisible,
  isSmSectionVisible,
  normalizeSmScreenConfig,
  orderedFlatKeys,
  orderedSmSections,
  orderedTwoColumnKeys,
  SmScreenSectionKey,
  SmTwoColumnGroup,
} from 'pages/Admin/AdminTools/Configuration/SecondaryMarketing/ScreenFields/types'
import { docCategory } from 'pages/LoanSubmission/constants'
import { ReminderDetailModal } from 'pages/Reminders/ReminderDetailModal'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { getAdminConfig, getAdminEmails, getInternalUserEmails } from 'services'
import {
  type OriginatorLockAiExtractData,
  getSecondaryMarketingScreenData,
  postSecondaryMarketingScreenData,
} from 'services/apis'
import { svgLoading } from 'stories/assets'
import {
  Button,
  Checkbox,
  FormTable,
  Input,
  Modal,
  PlainTable,
  Tooltip,
} from 'stories/components'
import { getPrice3decimal, removeComma, thousandSeperatorWithPrefix, useTitle } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { FeeDialog } from '../ClosingCalendar/FeeDialog'
import {
  buyPricingFields,
  defaultMarkupInputs,
  defaultPricingAdjustmentInputs,
  investorCommitmentFields,
  markupHeader,
  originatorLockFields,
  priceHeader,
  reminderDateFields,
  sellPricingFields,
} from './constant'
import { OriginatorLockAiExtractModal } from './OriginatorLockAiExtractModal'

const SM_SCREEN_CONFIG_KEY = 'secondaryMarketingScreenConfig'

export default function SecondaryMarketingScreen({ child = false }: { child?: boolean }) {
  useTitle(`Secondary Marketing - ${companyName}`)

  const [action, setAction] = useState('')
  const [IDs, setIDs] = useState<any>({})
  const [data, setData] = useState<any>({
    LoanWith: 365000,
    Pricing: {
      Buy: [],
      Sell: [],
    },
    Markup: [],
    BuyPriceBase: '',
    BuySRP: '',
    ARMMargin: '',
    IntRate: '',
    InvestorPriceBase: '',
    InvestorSRP: '',
    InvestorARMMargin: '',
    InvestorIntRate: '',
  })
  const [priceModal, setPriceModal] = useState('')
  const [buyProfit, setBuyProfit] = useState(0)
  const [sellProfit, setSellProfit] = useState(0)
  const [investors, setInvestors] = useState([])
  const [changed, setChanged] = useState(false)
  const [isBrokerModalOpen, setIsBrokerModalOpen] = useState<boolean>(false)
  const [feeData, setFeeData] = useState<any>([])
  const [feeKey, setFeeKey] = useState('')
  const [investorFees, setInvestorFees] = useState<Record<string, any>>({})
  const [userEmails, setUserEmails] = useState<string[]>([])
  const [reminderModal, setReminderModal] = useState<{ date: string } | null>(null)
  const [showLockAiExtract, setShowLockAiExtract] = useState(false)
  const [lockAiEnabled, setLockAiEnabled] = useState(false)
  const [screenConfig, setScreenConfig] = useState<ISmScreenConfig>(emptySmScreenConfig())

  const loanNumber = setLoanNumber()
  const { hasPermission } = usePermissions()
  const canLockAiExtract = hasPermission('Loan.SecondaryMarketing.AIExtract') && lockAiEnabled

  const onSync = async () => {
    setAction('sync')
    setChanged(false)
    await postSecondaryMarketingScreenData({ IDs, data })
    setAction('')
  }

  const initData = async () => {
    setAction('init')
    const res = await getSecondaryMarketingScreenData()
    setAction('')
    if (res.success) {
      if (res.data.data) setData(res.data.data)
      setIDs(res.data.IDs)
      setInvestors(res.data.investors)
      setInvestorFees(res.data.investorFees)
    }
  }

  const renderReminderIcon = (fieldKey: string) => (
    <span
      className="cursor-pointer text-shade-blue hover:opacity-70"
      onClick={() => setReminderModal({ date: data[fieldKey] || '' })}
    >
      <BellIcon className="w-4 h-4" />
    </span>
  )

  useEffect(() => {
    setLoanNumber()
    initData()
    Promise.all([getAdminEmails(), getInternalUserEmails()]).then(([adminEmails, internalEmails]) =>
      setUserEmails(Array.from(new Set([...adminEmails, ...internalEmails]))),
    )
    getAdminConfig(SM_SCREEN_CONFIG_KEY).then((res) => setScreenConfig(normalizeSmScreenConfig(res)))
  }, [])

  useEffect(() => {
    if (!hasPermission('Loan.SecondaryMarketing.AIExtract')) {
      setLockAiEnabled(false)
      return
    }
    getAdminConfig(SettingKey.GLOBAL_SETTINGS).then((settings: any) => {
      setLockAiEnabled(!!settings?.secondaryMarketingAiAutofillEnabled)
    })
  }, [hasPermission])

  const changeData = (key: string, value: any) => {
    const temp = cloneDeep(data)
    temp[key] = value
    if (['BuyPricedInvestor', 'PricedInvestor'].includes(key)) {
      const feeKey = key === 'BuyPricedInvestor' ? 'BuyFees' : 'SellFees'
      if (investorFees[value]?.length) temp[feeKey] = investorFees[value]
    }
    setChanged(true)
    setData(temp)
  }

  const applyOriginatorLockAiExtract = (extracted: OriginatorLockAiExtractData) => {
    const temp = cloneDeep(data)
    let applied = 0
    const numberKeys = ['LockDays', 'LockExtension1Days', 'LockExtension2Days', 'LockExtension3Days'] as const
    const dateKeys = [
      'LockStartDate',
      'LockExpirationDate',
      'LockCanceledDate',
      'UnextendedLockExpDate',
      'RegulatoryLockDateOV',
    ] as const
    const textKeys = ['BuyCommitmentNo'] as const

    numberKeys.forEach((key) => {
      const value = extracted[key]
      if (value == null || value === ('' as any)) return
      temp[key] = String(value)
      applied += 1
    })
    dateKeys.forEach((key) => {
      const value = extracted[key]
      if (value == null || value === '') return
      temp[key] = value
      applied += 1
    })
    textKeys.forEach((key) => {
      const value = extracted[key]
      if (value == null || value === '') return
      temp[key] = value
      applied += 1
    })
    if (extracted.LockFloating != null) {
      temp.LockFloating = extracted.LockFloating
      applied += 1
    }

    if (!applied) {
      toast('No lock fields could be applied from the document.', { type: 'warning' })
      return
    }
    setData(temp)
    setChanged(true)
    toast(`Applied ${applied} field${applied === 1 ? '' : 's'} from the lock sheet. Review and Save Changes.`, {
      type: 'info',
    })
  }

  const onSubmit = (currentId: any, values: Record<string, any>) => {
    return new Promise((resolve) => {
      let temp = cloneDeep(data)
      if (!currentId) {
        values = {
          id: Date.now(),
          ...values,
        }
        temp['Markup'].push(values)
      } else {
        temp['Markup'].map((item: any, index: number) => {
          if (item.id === currentId) temp['Markup'][index] = values
        })
      }
      setData(temp)
      setChanged(true)
      resolve(values)
    })
  }

  const onRemove = async (id: any) => {
    return new Promise((resolve) => {
      let newMarkup: any = []
      data['Markup'].map((item: any) => {
        if (item.id !== id) newMarkup.push(item)
      })
      let temp = cloneDeep(data)
      temp['Markup'] = newMarkup
      setData(temp)
      resolve(newMarkup)
    })
  }

  const onPriceSubmit = (currentId: any, values: Record<string, any>) => {
    return new Promise((resolve) => {
      let temp = cloneDeep(data)
      if (!currentId) {
        values = {
          id: Date.now(),
          ...values,
        }
        temp['Pricing'][priceModal].push(values)
      } else {
        temp['Pricing'][priceModal].map((item: any, index: number) => {
          if (item.id === currentId) temp['Pricing'][priceModal][index] = values
        })
      }
      setData(temp)
      resolve(values)
    })
  }

  const onPriceRemove = async (id: any) => {
    return new Promise((resolve) => {
      let newMarkup: any = []
      data['Pricing'][priceModal].map((item: any) => {
        if (item.id !== id) newMarkup.push(item)
      })
      let temp = cloneDeep(data)
      temp['Pricing'][priceModal] = newMarkup
      setData(temp)
      resolve(newMarkup)
    })
  }

  const markupTotal = useMemo(() => {
    let totalPercent = 0
    let totalAmount = 0
    let markup = data['Markup']
    markup?.map((item: any) => {
      totalPercent += removeComma(item.MarkupPercent)
      totalAmount += removeComma(item.MarkupAmount)
    })
    return (
      <div className="flex flex-wrap gap-6">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[13px]">Total Percent:</span>
          <span className="font-bold">{totalPercent}%</span>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[13px]">Total Amount:</span>
          <span className="font-bold">{getPrice3decimal(totalAmount)}$</span>
        </div>
      </div>
    )
  }, [data['Markup']])

  const feeAmount = (key: string) => {
    let sum = 0
    data[key]?.map((item: any) => {
      sum += Number(removeComma(item.amount))
    })
    return sum
  }

  const totalGain = useMemo(() => {
    // Side profits already include that side's fees.
    const totalProfitAmount = sellProfit - buyProfit
    const totalProfitPercent = (totalProfitAmount * 100) / data['LoanWith']
    return (
      <table className="text-[14px] w-full">
        <thead>
          <tr>
            <th className="border"></th>
            <th className="border px-2 py-1">Percent</th>
            <th className="border px-2 py-1">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border px-2 py-1">Sell Side Net Profit</td>
            <td className="border px-2 py-1"></td>
            <td className="border px-2 py-1">${getPrice3decimal(sellProfit.toFixed(3))}</td>
          </tr>
          <tr>
            <td className="border px-2 py-1">Buy Side Net Profit</td>
            <td className="border px-2 py-1"></td>
            <td className="border px-2 py-1">${getPrice3decimal(buyProfit.toFixed(3))}</td>
          </tr>
          <tr className="font-bold">
            <td className="border px-2 py-1 text-center">Total</td>
            <td className="border px-2 py-1">{totalProfitPercent.toFixed(4)}%</td>
            <td className="border px-2 py-1">${getPrice3decimal(totalProfitAmount.toFixed(3))}</td>
          </tr>
        </tbody>
      </table>
    )
  }, [sellProfit, buyProfit, data['BuyFees'], data['SellFees']])

  const copyPricingInfo = (type: string) => {
    let temp = cloneDeep(data)
    if (type === 'Buy') {
      temp['Pricing']['Buy'] = cloneDeep(temp['Pricing']['Sell'])
      temp.BuyPriceBase = temp.InvestorPriceBase
      temp.BuySRP = temp.InvestorSRP
      temp.ARMMargin = temp.InvestorARMMargin
      temp.IntRate = temp.InvestorIntRate
    }
    if (type === 'Sell') {
      temp['Pricing']['Sell'] = cloneDeep(temp['Pricing']['Buy'])
      temp.InvestorPriceBase = temp.BuyPriceBase
      temp.InvestorSRP = temp.BuySRP
      temp.InvestorARMMargin = temp.ARMMargin
      temp.InvestorIntRate = temp.IntRate
    }
    setData(temp)
  }

  const renderPricing = (type: string) => {
    let priceBase = 'BuyPriceBase'
    let srp = 'BuySRP'
    let margin = 'ARMMargin'
    let rate = 'IntRate'

    if (type === 'Sell') {
      priceBase = 'InvestorPriceBase'
      srp = 'InvestorSRP'
      margin = 'InvestorARMMargin'
      rate = 'InvestorIntRate'
    }

    const adjustments = data['Pricing'][type]
    let baseMargin = removeComma(data[margin])
    let baseRate = removeComma(data[rate])
    let netPrice = removeComma(data[priceBase]) + removeComma(data[srp])
    let netSRP = removeComma(data[srp])
    let baseProfit = 0
    let netProfit = 0
    let adjustProfit = 0

    adjustments?.map((item: any) => {
      baseMargin -= removeComma(item.MarginPercent)
      baseRate -= removeComma(item.IntRatePercent)
      netPrice += removeComma(item.PricePercent)
      adjustProfit += removeComma(item.FeeAmount)
      if (item.PriceAdjustmentType == 1) netSRP += removeComma(item.PricePercent)
    })

    baseProfit = (data['LoanWith'] * (netPrice - 100)) / 100
    netProfit = baseProfit - adjustProfit + feeAmount(`${type}Fees`)
    if (type === 'Buy' && netProfit != buyProfit) {
      setBuyProfit(netProfit)
    }
    if (type === 'Sell' && netProfit != sellProfit) {
      setSellProfit(netProfit)
    }

    const RateSpreadKey = `${type}SideRateSpread`
    const ExitFeeKey = `${type}SideExitFee`

    return (
      <div className="my-2">
        <div className="flex justify-between mb-2 text-shade-blue flex-wrap gap-4">
          <div
            className="flex gap-1 items-center hover:underline cursor-pointer"
            onClick={() => {
              copyPricingInfo(type)
            }}
          >
            <DocumentDuplicateIcon className="w-4 h-4"></DocumentDuplicateIcon>
            <span>Copy from {type === 'Buy' ? 'Sell' : 'Buy'}-Side</span>
          </div>
          <div
            className="flex gap-1 items-center hover:underline cursor-pointer"
            onClick={() => {
              setPriceModal(type)
            }}
          >
            <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
            <span>Edit Adjustment</span>
          </div>
        </div>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-stone-50">
              <th className="border px-2 py-1"></th>
              <th className="border px-2 py-1">Margin</th>
              <th className="border px-2 py-1">Rate</th>
              <th className="border px-2 py-1">Price</th>
              <th className="border px-2 py-1">Profit</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border px-2 py-1">Base</td>
              <td className="border px-2 py-1">{baseMargin.toFixed(3)}</td>
              <td className="border px-2 py-1">{baseRate.toFixed(3)}</td>
              <td className="border px-2 py-1">
                <div className="-mb-4 w-[75px]">
                  <Input
                    className="mb-0 w-full"
                    value={data[priceBase]}
                    type="number"
                    onChange={(value) => changeData(priceBase, value)}
                  />
                </div>
              </td>
              <td className="border px-2 py-1">{getPrice3decimal(baseProfit.toFixed(3))}</td>
            </tr>
            {adjustments?.map((item: any, index: number) => {
              return (
                <tr key={index}>
                  <td className="border px-2 py-1">{item.Description}</td>
                  <td className="border px-2 py-1">{Number(item.MarginPercent).toFixed(3)}</td>
                  <td className="border px-2 py-1">{Number(item.IntRatePercent).toFixed(3)}</td>
                  <td className="border px-2 py-1">{Number(item.PricePercent).toFixed(3)}</td>
                  <td className="border px-2 py-1">{getPrice3decimal(Number(item.FeeAmount).toFixed(3))}</td>
                </tr>
              )
            })}
            <tr>
              <td className="border px-2 py-1">Base SRP</td>
              <td className="border px-2 py-1"></td>
              <td className="border px-2 py-1"></td>
              <td className="border px-2 py-1">
                <div className="-mb-4 w-[75px]">
                  <Input
                    className="mb-0 w-full"
                    value={data[srp]}
                    type="number"
                    onChange={(value) => changeData(srp, value)}
                  />
                </div>
              </td>
              <td className="border px-2 py-1"></td>
            </tr>
            <tr className="">
              <td className="border px-2 py-1 border-t-gray-500 border-t-[2px]">Net</td>
              <td className="border px-2 py-1 border-t-gray-500 border-t-[2px]">
                <div className="-mb-4 w-[75px]">
                  <Input
                    className="mb-0 w-full"
                    value={data[margin]}
                    type="number"
                    onChange={(value) => changeData(margin, value)}
                  />
                </div>
              </td>
              <td className="border px-2 py-1 border-t-gray-500 border-t-[2px]">
                <div className="-mb-4 w-[75px]">
                  <Input
                    className="mb-0 w-full"
                    value={data[rate]}
                    type="number"
                    onChange={(value) => changeData(rate, value)}
                  />
                </div>
              </td>
              <td className="border px-2 py-1 border-t-gray-500 border-t-[2px]">{netPrice.toFixed(3)}</td>
              <td className="border px-2 py-1 border-t-gray-500 border-t-[2px]">
                {getPrice3decimal(netProfit.toFixed(3))}
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Net SRP</td>
              <td className="border px-2 py-1"></td>
              <td className="border px-2 py-1"></td>
              <td className="border px-2 py-1">{netSRP.toFixed(3)}</td>
              <td className="border px-2 py-1"></td>
            </tr>
          </tbody>
        </table>
        <div className={`grid gap-4 md:grid-cols-2 grid-cols-1 mt-4`}>
          <div className={`input md:col-span-1}`}>
            <RenderInput
              input={{
                title: 'Rate Spread',
                inputType: 'text',
                type: 'number',
                value: data[RateSpreadKey],
              }}
              Key={RateSpreadKey}
              onChange={changeData}
            />
          </div>
          <div className={`input md:col-span-1}`}>
            <RenderInput
              input={{
                title: 'Exit Fee',
                inputType: 'text',
                type: 'number',
                value: data[ExitFeeKey],
              }}
              Key={ExitFeeKey}
              onChange={changeData}
            />
          </div>
        </div>
      </div>
    )
  }

  const onFeeSubmit = (value: any) => {
    const temp = cloneDeep(data)
    temp[feeKey] = value
    setData(temp)
  }

  const onClose = () => {
    setIsBrokerModalOpen(false)
  }

  const renderFees = (type: string) => {
    const key = `${type}Fees`

    return (
      <div className="relative overflow-auto" key={type}>
        <span className="flex justify-between">
          Fees{' '}
          <PencilSquareIcon
            className="w-4 h-4 cursor-pointer"
            onClick={() => {
              setIsBrokerModalOpen(true)
              setFeeData(data[key] || [])
              setFeeKey(key)
            }}
          />
        </span>
        <PlainTable
          header={['No', 'Title', 'Amount']}
          tdClass="px-6 py-4 whitespace-nowrap capitalize"
          data={(data[key] || []).map((item: any, index: number) => [
            index + 1,
            item.title,
            thousandSeperatorWithPrefix(item.amount),
          ])}
          key={type}
        />
      </div>
    )
  }

  const sectionOrder = useMemo(() => orderedSmSections(screenConfig), [screenConfig])

  const renderTwoColumnSection = (
    section: 'originatorLock' | 'investorCommitment',
    fieldMap: Record<string, Record<string, any>>,
    headerExtra?: ReactNode,
    footer?: ReactNode,
    className = '',
  ) => {
    if (!isSmSectionVisible(screenConfig, section)) return null
    const titleFallback = section === 'originatorLock' ? 'Originator Lock Info' : 'Investor Commitment Info'
    const groups: SmTwoColumnGroup[] = ['leftGroup', 'rightGroup']
    return (
      <div className={`shadow rounded border max-w-[900px] ${className}`.trim()}>
        <div className="bg-gray-200 round-t py-1 px-4 flex items-center justify-between gap-3">
          <span>{getSmSectionTitle(screenConfig, section, titleFallback)}</span>
          {headerExtra}
        </div>
        <div className="p-3">
          <div className="grid md:grid-cols-2 gap-6">
            {groups.map((groupKey) => {
              const fields = fieldMap[groupKey] || {}
              const keys = orderedTwoColumnKeys(screenConfig, section, groupKey, fields).filter((key) =>
                isSmFieldVisible(screenConfig, key),
              )
              return (
                <div className="grid md:grid-cols-3 gap-2 h-fit" key={groupKey}>
                  {keys.map((key) => {
                    const base = fields[key]
                    if (!base) return null
                    const input: any = {
                      ...base,
                      title: getSmFieldTitle(screenConfig, key, base.title),
                      value: key === 'RegulatoryLockDate' ? data['LockStartDate'] : data[key],
                    }
                    if (['InvestorCode', 'MandatoryInvestor', 'ExcludedInvestor'].includes(key)) {
                      input.options = investors
                    }
                    return (
                      <div className={`md:col-span-${input.span ? input.span : 3}`} key={`${groupKey}-${key}`}>
                        <RenderInput
                          input={{
                            ...input,
                            ...(reminderDateFields.includes(key)
                              ? { additionalElements: renderReminderIcon(key) }
                              : {}),
                          }}
                          Key={key}
                          onChange={changeData}
                        />
                      </div>
                    )
                  })}
                </div>
              )
            })}
            {footer}
          </div>
        </div>
      </div>
    )
  }

  const renderMarkupSection = () => {
    if (!isSmSectionVisible(screenConfig, 'markup')) return null
    return (
      <div className="shadow rounded border max-w-[900px]">
        <div className="bg-gray-200 round-t py-1 px-4">
          {getSmSectionTitle(screenConfig, 'markup', 'Margin / Markup')}
        </div>
        <div className="p-3">
          {markupTotal}
          <div className="mt-3 overflow-auto pb-2">
            <FormTable
              header={markupHeader}
              inputs={defaultMarkupInputs}
              defaultData={data['Markup']}
              onSubmit={onSubmit}
              onRemove={onRemove}
              addText="Add New Margin / Markup"
            />
          </div>
        </div>
      </div>
    )
  }

  const renderPricingSection = () => {
    if (!isSmSectionVisible(screenConfig, 'pricing')) return null
    const buyKeys = orderedFlatKeys(screenConfig, 'buyPricing', buyPricingFields).filter((key) =>
      isSmFieldVisible(screenConfig, key),
    )
    const sellKeys = orderedFlatKeys(screenConfig, 'sellPricing', sellPricingFields).filter((key) =>
      isSmFieldVisible(screenConfig, key),
    )
    return (
      <div className="shadow rounded mt-5 border max-w-[1000px]">
        <div className="bg-gray-200 round-t py-1 px-4">
          {getSmSectionTitle(screenConfig, 'pricing', 'Pricing and Profit')}
        </div>
        <div className="p-3">
          <div className="overflow-auto">
            <table className="text-[14px] w-full">
              <thead>
                <tr className="bg-blue-50">
                  <th className="w-[50%] border py-1 border-r-gray-500 px-2 border-r-[3px]">Buy Side</th>
                  <th className="w-[50%] border py-1">Sell Side</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border  border-r-gray-500 px-2 border-r-[3px] align-baseline">
                    <div>
                      <div className="h-fit py-2">
                        {buyKeys.map((key) => {
                          const base: any = buyPricingFields[key]
                          if (!base) return null
                          const input: any = {
                            ...base,
                            title: getSmFieldTitle(screenConfig, key, base.title),
                            value: data[key],
                          }
                          if (key === 'BuyPricedInvestor') input.options = investors
                          return (
                            <div className="py-1" key={key}>
                              <RenderInput input={input} Key={key} onChange={changeData} />
                            </div>
                          )
                        })}
                      </div>
                      {renderPricing('Buy')}
                      {renderFees('Buy')}
                    </div>
                  </td>
                  <td className="border px-2 align-baseline">
                    <div>
                      <div className="h-fit py-2">
                        {sellKeys.map((key) => {
                          const base: any = sellPricingFields[key]
                          if (!base) return null
                          const input: any = {
                            ...base,
                            title: getSmFieldTitle(screenConfig, key, base.title),
                            value: data[key],
                          }
                          if (key === 'PricedInvestor') input.options = investors
                          return (
                            <div className="py-1" key={key}>
                              <RenderInput input={input} Key={key} onChange={changeData} />
                            </div>
                          )
                        })}
                      </div>
                      {renderPricing('Sell')}
                      {renderFees('Sell')}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-4">{totalGain}</div>
        </div>
      </div>
    )
  }

  const renderSection = (key: SmScreenSectionKey) => {
    switch (key) {
      case 'originatorLock':
        return (
          <div key={key} className="md:col-span-12">
            {renderTwoColumnSection(
              'originatorLock',
              originatorLockFields,
              canLockAiExtract ? (
                <span
                  className="text-shade-blue cursor-pointer hover:underline text-sm"
                  onClick={() => setShowLockAiExtract(true)}
                >
                  Extract from PDF
                </span>
              ) : undefined,
            )}
          </div>
        )
      case 'investorCommitment':
        return (
          <div key={key} className="md:col-span-12">
            {renderTwoColumnSection(
              'investorCommitment',
              investorCommitmentFields,
              undefined,
              <div className="col-span-full">
                <RenderInput
                  input={{
                    inputType: 'FileTable',
                    title: 'Attachment Files',
                    required: false,
                    filePath: `loan/${loanNumber}/secondaryMarketing`,
                    showCategory: true,
                    showStatus: true,
                    multiple: true,
                    categories: docCategory().filter((v: string) => !!v),
                    additionalActions: [],
                    value: data.documents,
                    additionalRowActions: (document: any, index: number, onChange: Function) => {
                      return (
                        <Tooltip message="Internal/External">
                          <Checkbox
                            title=""
                            id={`side-document-intext-${document.id}`}
                            checked={document.intext}
                            onChange={(value: any) => onChange('intext', index, value)}
                          />
                        </Tooltip>
                      )
                    },
                  }}
                  Key="documents"
                  key="documents"
                  onChange={changeData}
                />
              </div>,
              'mt-4',
            )}
          </div>
        )
      case 'markup':
        return (
          <div key={key} className="md:col-span-12">
            {renderMarkupSection()}
          </div>
        )
      case 'pricing':
        return (
          <div key={key} className="md:col-span-12">
            {renderPricingSection()}
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className={`SecondaryMarketingScreen-container ${!child && 'py-6 px-2'}`}>
      {!child && <Overview title="Secondary Marketing" />}
      <SaveChanges show={changed} onSave={onSync} />
      <div className="max-w-screen-2xl m-auto">
        <div className={`relative bg-white ${child ? '' : 'shadow1 rounded mb-4 md:p-6 p-2'}`}>
          <LayoutLoading show={action === 'init' || action === 'sync'} />
          <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
            <span>Secondary Marketing</span>
            {action === 'init' ||
              (action === 'sync' && <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin ml-2" />)}
          </h2>
          <div className="grid md:grid-cols-12 gap-5">
            {sectionOrder.map((key) => renderSection(key))}
            <div className="md:col-span-12">
              <div className="mt-6 flex justify-center max-w-[1000px]">
                <div className="w-[200px]">
                  <Button loading={action === 'sync'} onClick={onSync} className="w-full">
                    Sync
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        title={`${priceModal} - Side Adjustments`}
        titleOkay=""
        titleCancel="Close"
        isOpen={priceModal !== ''}
        lastUpdatedAt={Date.now()}
        onClose={() => {
          setPriceModal('')
        }}
      >
        <div className="overflow-auto">
          <FormTable
            header={priceHeader}
            inputs={defaultPricingAdjustmentInputs}
            defaultData={data['Pricing'][priceModal]}
            onSubmit={onPriceSubmit}
            onRemove={onPriceRemove}
            addText="Add New Adjustment"
          />
        </div>
      </Modal>
      {isBrokerModalOpen && (
        <FeeDialog modalTitle="Update Fees" onClose={onClose} onSubmit={onFeeSubmit} data={feeData} />
      )}
      {reminderModal !== null && (
        <ReminderDetailModal
          reminderId={-1}
          userEmails={userEmails}
          onModalClose={() => setReminderModal(null)}
          onModalOk={() => setReminderModal(null)}
          loanNumber={Number(loanNumber)}
          templateDefaults={{ date: reminderModal.date }}
        />
      )}
      {canLockAiExtract && showLockAiExtract && (
        <OriginatorLockAiExtractModal
          isOpen={showLockAiExtract}
          loanNumber={Number(loanNumber)}
          onClose={() => setShowLockAiExtract(false)}
          onApply={applyOriginatorLockAiExtract}
        />
      )}
    </div>
  )
}
